import { format } from "date-fns";

import { getTimeRangeFilter, TIME_RANGE_LABELS, TimeRange } from "../../../utils/timerange";
import { AttributeExamples } from "../types";
import { formatUserNameFromEmail } from "./formatUserName";
import { AgentPerformanceAnalysisData } from "./markdownConverter";

interface BuildPerformanceAnalysisDataParams {
  attributeStatsContent: string;
  attributeExamples: AttributeExamples[];
  selectedUserEmail?: string;
  lensName: string;
  timeRange: TimeRange;
  timezone: string;
}

const formatAnalysisDate = (timeRange: TimeRange, timezone: string): string => {
  const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);
  const start = new Date(startDate);
  const end = new Date(endDate);

  const formatDate = (date: Date) => format(date, "MMM d, yyyy");

  const label = TIME_RANGE_LABELS[timeRange] || "Current Period";

  if (timeRange === "allTime") {
    return `All Time (through ${formatDate(end)})`;
  }

  if (timeRange.startsWith("custom:")) {
    return `${formatDate(start)} - ${formatDate(end)}`;
  }

  return `${label} (${formatDate(start)} - ${formatDate(end)})`;
};

export const buildPerformanceAnalysisData = ({
  attributeStatsContent,
  attributeExamples,
  selectedUserEmail,
  lensName,
  timeRange,
  timezone
}: BuildPerformanceAnalysisDataParams): AgentPerformanceAnalysisData => {
  if (!selectedUserEmail) {
    throw new Error("selectedUserEmail is required for performance analysis data.");
  }

  const statsData = JSON.parse(attributeStatsContent);
  const byUserStats = statsData.byUser || [];

  const userEmail = selectedUserEmail;

  const totalAgents = byUserStats.length;

  const userAttributeStats = byUserStats.filter((stat: any) => stat.user_email === userEmail);

  const attributeMap = new Map<string, any>();
  attributeExamples.forEach(attributeExample => {
    const stat = userAttributeStats.find((s: any) => s.attr_name === attributeExample.attribute);
    if (stat) {
      attributeMap.set(attributeExample.attribute, {
        stat,
        attributeExample
      });
    }
  });

  const totalConversations = userAttributeStats.length > 0
    ? userAttributeStats[0].scored_count
    : 0;

  const analysisDate = formatAnalysisDate(timeRange, timezone);

  const attrExampleCounts = Array.from(attributeMap.entries()).map(([name, data]) =>
    `${name}: ${data.attributeExample.lowScoring.length} low, ${data.attributeExample.highScoring.length} high`);
  console.log("[buildPerformanceAnalysisData]", `attributes: ${attributeMap.size}`, attrExampleCounts);

  const analysisData: AgentPerformanceAnalysisData = {
    analysis_date: analysisDate,
    total_conversations: totalConversations,
    lens_name: lensName,
    total_attributes: attributeMap.size,
    attributes: Array.from(attributeMap.entries()).map(([attrName, data]) => {
      const { stat, attributeExample } = data;

      return {
        attribute_name: attrName,
        attribute_definition: undefined,
        your_stats: {
          avg_score: stat.avg_score,
          min_score: stat.min_score,
          max_score: stat.max_score,
          median_score: stat.median_score,
          stddev_score: stat.stddev_score,
          scored_count: stat.scored_count,
          not_scored_count: stat.not_scored_count
        },
        team_stats: {
          avg_score: stat.group_avg_score,
          min_score: stat.group_min_score,
          max_score: stat.group_max_score,
          median_score: stat.group_median_score,
          stddev_score: stat.group_stddev_score,
          scored_count: stat.group_scored_count,
          not_scored_count: stat.group_not_scored_count
        },
        your_vs_team: {
          delta: stat.delta_vs_group_avg,
          percentage_of_team: stat.pct_of_group_avg,
          percentage_point_diff: stat.pct_delta_vs_group_avg
        },
        best_performer: {
          name: formatUserNameFromEmail(stat.best_user_email),
          email: stat.best_user_email,
          avg_score: stat.best_user_avg_score,
          delta: stat.delta_vs_best,
          percentage_of_best: stat.pct_of_best,
          percentage_point_diff: stat.pct_delta_vs_best
        },
        ranking: {
          rank: stat.rank,
          total_agents: totalAgents,
          percentile: stat.percentile
        },
        trend: {
          prev_avg_score: stat.prev_avg_score,
          delta: stat.delta_vs_prev_avg,
          percentage_of_prev: stat.pct_of_prev_avg,
          percentage_point_change: stat.pct_delta_vs_prev_avg
        },
        low_scoring_examples: attributeExample.lowScoring.map((item: any) => ({
          interaction_id: item.interaction_id,
          score: item.score_value,
          summary: item.summary,
          details: item.details,
          score_justification: item.score_justification,
          next_time: item.next_time
        })),
        high_scoring_examples: attributeExample.highScoring.map((item: any) => ({
          interaction_id: item.interaction_id,
          score: item.score_value,
          summary: item.summary,
          details: item.details,
          score_justification: item.score_justification
        }))
      };
    })
  };

  return analysisData;
};
